/* Verbatim port of entityHeadline, planeVerdicts, matrixEntities,
 * platformOf from service/review/index.html (~1033-1086, ~1291-1386).
 *
 * PARAMETERIZED (rule a): all four read the global `STATE` (directly, or
 * transitively via searchHeadline/aiHeadline/peerRank/entityHeadline) —
 * each now takes `state: AppPayloadState` as an explicit first parameter.
 * platformOf does not read STATE and keeps its original single-arg
 * signature.
 */
import { esc, listJoin, pct, slugify } from '../lib/format.ts';
import { aiHeadline } from './ai.ts';
import { peerRank, searchHeadline } from './search.ts';
import type { AppPayloadState } from './state-shape.ts';
import type { ClaimedState, EntityKind, NormalizedAsset, RawEdge } from '../types/contract.ts';

export interface EntityHeadline {
  kpPresent: boolean;
  kpClaimed: ClaimedState;
  wdPresent: boolean;
  qid: string | null;
  fix: number;
  confirmed: number;
  total: number;
  weak: number;
  bridges: number;
  orphans: number;
  measured: boolean;
  issues: number;
}

export interface PlaneVerdict {
  plane: string;
  st: 'win' | 'hold' | 'lose';
  /** either a bare number-as-string or `"<n><small>...</small>"` — see
   * views/exec.ts's numT() */
  num: string;
  numLabel: string;
  read: string;
  anchor: string;
}

/** One row of the §4 matrix: an entity plus its asset-membership predicate. */
export interface MatrixEntityRow {
  key: string;
  name: string | null;
  role: string;
  kind?: EntityKind;
  domain?: string | null;
  match: (a: NormalizedAsset) => boolean;
}

/* Entity plane */
export function entityHeadline(state: AppPayloadState): EntityHeadline | null {
  const wd = state.wikidata, kp = state.knowledgePanel;
  if (!wd && !kp) return null;
  const ws = (wd && wd.worksheet) || [];
  const fix = ws.filter((w) => w.state === 'missing' || w.state === 'stale').length;
  const confirmed = ws.filter((w) => w.state === 'confirmed').length;
  const lg = state.linkGraph;
  const weak = ((lg && lg.weakChains) || []).length;
  const bridges = ((lg && lg.bridges) || []).length;
  const orphans = ((lg && lg.orphans) || []).length;
  return { kpPresent: !!(kp && kp.present), kpClaimed: kp ? kp.claimed : 'unknown',
           wdPresent: !!(wd && wd.present), qid: (wd && wd.qid) || null, fix, confirmed, total: ws.length,
           weak, bridges, orphans, measured: !!(lg && (lg.pageFacts || lg.bridges)),
           issues: ((kp && kp.issues) || []).length };
}

/* Plane verdicts (exec summary + section headers share these) */
export function planeVerdicts(state: AppPayloadState): PlaneVerdict[] {
  const out: PlaneVerdict[] = [];
  const sh = searchHeadline(state);
  // rows.length > 0 iff best/worst non-null (searchHeadline sets them from a
  // non-empty rows array) — narrowing on all three keeps TS honest without
  // changing which runs render the verdict.
  const first = sh && sh.rows[0];
  if (sh && first && sh.best && sh.worst && first.slots != null
      && sh.worst.owned != null && sh.best.owned != null){
    const half = first.slots / 2;
    const st = sh.worst.owned >= half ? 'win' : sh.best.owned >= half ? 'hold' : 'lose';
    out.push({ plane:'Search results', st,
      num: `${sh.avg}<small>/${first.slots}</small>`,
      numLabel: `page-1 results owned, avg across ${sh.rows.length} cities (primary term)`,
      read: `Strongest in ${listJoin(sh.bestLocs)} (${sh.best.owned}/${sh.best.slots}), weakest in ${listJoin(sh.worstLocs)} (${sh.worst.owned}/${sh.worst.slots}).` +
            (sh.negLocs.length ? ` Undesirable results on page 1 in ${listJoin(sh.negLocs)}.` : ' No undesirable results on page 1.'), anchor:'#s1' });
  }
  const ah = aiHeadline(state);
  if (ah){
    const pr = peerRank(state);
    // HONESTY (coverage-honesty audit P2-1): when source ownership wasn't
    // measured (aiHeadline preserves null), the verdict tile must not show a
    // fabricated "0%" nor let that zero drive the win/hold/lose state. Render
    // an em-dash + "not measured" label; derive the state from the peer-rank
    // signal that IS measured (or 'hold' when neither is present).
    const ownMeasured = ah.ownership != null;
    const st = ah.ownership != null
      ? (ah.ownership >= .5 ? 'win' : (pr && pr.rank === pr.of) ? 'lose' : 'hold')
      : ((pr && pr.rank === pr.of) ? 'lose' : 'hold');
    out.push({ plane:'AI narrative', st,
      num: ownMeasured ? `${pct(ah.ownership)}<small>%</small>` : '—',
      numLabel: ownMeasured ? `of AI citations come from client-controlled sources` : `source ownership not measured this run`,
      read: (pr ? `On own-domain citations alone, ${pr.rank === pr.of ? 'last' : '#'+pr.rank} in the ${pr.of}-brand tracked set (${pct(pr.client.selfCitationShare)}% vs ${esc(pr.best.name)} ${pct(pr.best.selfCitationShare)}%). ` : '') +
            (ah.unanimousNeg ? `${ah.unanimousNeg} negative theme${ah.unanimousNeg>1?'s':''} repeated by all ${ah.engines} engines.` : ''), anchor:'#s3' });
  }
  const eh = entityHeadline(state);
  if (eh){
    const st = !eh.kpPresent ? 'lose' : (eh.kpClaimed === 'yes' && eh.fix === 0) ? 'win' : 'hold';
    const kpRead = !eh.kpPresent ? 'No Knowledge Panel yet.'
      : eh.kpClaimed === 'yes' ? 'Knowledge Panel present and claimed.'
      : eh.kpClaimed === 'unknown' ? 'Knowledge Panel present; claim status unverified.'
      : 'Knowledge Panel present but not claimed.';
    out.push({ plane:'Entity & Knowledge Panel', st,
      num: eh.measured && eh.bridges ? `${eh.bridges}` : `${eh.fix}`,
      numLabel: eh.measured && eh.bridges ? `missing cross-links found by the link crawl` : `Wikidata claims to fix or add (of ${eh.total} reviewed)`,
      read: kpRead + (eh.measured && eh.bridges ? ` ${eh.fix} Wikidata claims to fix${eh.orphans ? `; ${eh.orphans} properties are orphans` : ''}.` : eh.weak ? ` ${eh.weak} official profiles lack authoritative cross-links.` : ''), anchor:'#s2' });
  }
  return out;
}

/* ── entity rows for the §4 matrix ── */
// The matrix inventories platforms a COMPANY or a NAMED PERSON can have a
// controllable footprint on (website, social profile, directory listing…).
// It is never a home for news coverage, forum threads, or catch-all "other"
// assets — those are MENTIONS of the entity, not a footprint of its own.
// The schema's scope enum (global/regional/product/subsidiary/executive)
// gives agents no bucket for "this is a controversy closely tied to an
// executive" (a lawsuit, a hearing, a killing), so in practice these land
// in scope:'executive' or scope:'subsidiary' anyway — producing rows like
// "Killing of Brian Thompson" with a parsed "role" of "Dec 2024". Filter
// defensively on assetType regardless of the scope the agent chose; this
// is what actually distinguishes an entity from a mention of one, and it
// costs nothing else in the product — news-coverage assets already have a
// correct home (the "News & media coverage" group in §4, and the full
// unfiltered inventory in the appendix).
export const NOT_A_FOOTPRINT: Set<string> = new Set(['news-coverage', 'forum', 'other']);
export const ENTITY_KIND_ROLE: Record<string, string> = { parent:'Parent company', subsidiary:'Subsidiary / brand',
  brand:'Brand', division:'Division', product:'Product',
  historical:'Former / historical', executive:'Executive' };

export function matrixEntities(state: AppPayloadState): MatrixEntityRow[] {
  // AUTHORITATIVE PATH — the research run declared its corporate map
  // (payload.entities). No title parsing, no scope inference: each entity
  // owns exactly the asset ids the researcher assigned it; the parent row
  // inherits everything unassigned that isn't a mention-scoped asset. This
  // is the fix for lawsuits/hearings rendering as "Subsidiary / brand" —
  // derivation from asset scopes can never classify those correctly.
  const em = state.entityMap;
  if (em){
    const assigned = new Set(em.flatMap((e) => e.kind === 'parent' ? [] : (e.assetIds || [])));
    return em.map((e, i) => {
      const idSet = new Set(e.assetIds || []);
      const role = e.role || ENTITY_KIND_ROLE[e.kind] || e.kind || '';
      const match = e.kind === 'parent'
        ? ((a: NormalizedAsset) => idSet.has(a.id) || (!assigned.has(a.id) && a.scope !== 'subsidiary' && a.scope !== 'executive'))
        : ((a: NormalizedAsset) => idSet.has(a.id));
      return { key:'em-'+i, name: e.name, role, kind: e.kind, domain: e.domain || null, match };
    });
  }
  const brand = state.brand;
  const brandNorm = slugify(brand).replace(/-/g,'');
  const coreAssets = new Set<string>();
  const subs = state.assets.filter((a) => a.scope === 'subsidiary' && !NOT_A_FOOTPRINT.has(a.assetType));
  const byId = new Map<string, NormalizedAsset>(state.assets.map((a) => [a.id, a]));

  // Group subsidiary-scoped assets into ONE row per subsidiary company.
  // Preferred signal: the edge graph — an official-profile-of/product-site-of/
  // subsidiary-of edge from one subsidiary asset to ANOTHER subsidiary asset
  // means "this is a profile/product of that company", not its own entity
  // (e.g. "Optum LinkedIn" official-profile-of "Optum"). Fallback: parse the
  // title, since agents don't follow one naming convention — "Optum —
  // LinkedIn", "Optum LinkedIn" (no separator), and "Optum Instagram" all
  // occur in practice, and without stripping the trailing platform word the
  // old dash-only split left every platform variant as its own fake "entity".
  const FAMILY_RELS = ['official-profile-of', 'product-site-of', 'subsidiary-of'];
  const hubOf = new Map<string, NormalizedAsset>(); // subsidiary asset id -> the hub asset it belongs to
  state.edges.forEach((e: RawEdge) => {
    if (!FAMILY_RELS.includes(e.relation)) return;
    const from = byId.get(e.from), to = byId.get(e.to);
    if (from && to && from.id !== to.id && from.scope === 'subsidiary' && to.scope === 'subsidiary')
      hubOf.set(from.id, to);
  });
  function resolveHub(a: NormalizedAsset): NormalizedAsset | null { // follow short chains to the ultimate hub; cap guards against cycles
    let cur = a, hops = 0;
    while (hops++ < 5){ const next = hubOf.get(cur.id); if (!next) break; cur = next; }
    return cur === a ? null : cur;
  }
  const PLATFORM_SUFFIX = /\s+(Official\s+Site|Home\s*page|Website|LinkedIn|Facebook|X\/Twitter|Twitter\/X|Twitter|X|Instagram|YouTube|TikTok|Wikipedia|Wikidata|App)\s*$/i;

  interface SubGroup { name?: string; names?: Record<string, number>; assets: NormalizedAsset[]; }
  const subGroups: Record<string, SubGroup> = {}; // key -> { name?, names?, assets:[] }
  const placed = new Set<string>();
  subs.forEach((a) => {
    const hub = resolveHub(a);
    if (!hub) return;
    const key = 'hub-' + hub.id;
    const g = subGroups[key] = subGroups[key] || { name: hub.title, assets: [] };
    if (!g.assets.includes(hub)) g.assets.push(hub);
    g.assets.push(a);
    placed.add(a.id); placed.add(hub.id);
  });
  subs.filter((a) => !placed.has(a.id)).forEach((a) => {
    let n = ((a.title||'').split(/—|–| - /)[0] ?? '').replace(/\(.*?\)/g,'').replace(PLATFORM_SUFFIX,'').trim();
    const nNorm = slugify(n).replace(/-/g,'');
    if (!n || nNorm.includes(brandNorm) || brandNorm.includes(nNorm)){ coreAssets.add(a.id); return; }
    const key = 'title-' + slugify(n).split('-').slice(0,2).join('-');
    const g = subGroups[key] = subGroups[key] || { names:{}, assets:[] };
    g.assets.push(a);
    g.names = g.names || {}; g.names[n] = (g.names[n]||0)+1;
  });

  const ents: MatrixEntityRow[] = [{ key:'core', name: brand, role:'Parent company',
    match: (a) => (a.scope !== 'subsidiary' && a.scope !== 'executive') || coreAssets.has(a.id) }];
  Object.entries(subGroups).forEach(([key, g]) => {
    const name = g.name || (Object.entries(g.names || {}).sort((a,b) => (b[1]-a[1]) || (a[0].length - b[0].length))[0]?.[0] ?? '');
    const members = g.assets;
    ents.push({ key:'sub-'+key, name, role:'Subsidiary / brand', match: (a) => members.includes(a) });
  });
  // executives: parse "<name> — <role>" robustly. C3 (2026-07-17 steer): the old
  // "first parenthetical" split truncated names that carry a mid-name nickname
  // ("Levi (Levy) HaLevi" → "Levi"; "Kenneth (Kenny) Rozenberg" → "Kenneth", with
  // the nickname mis-read as the role). Now: split role off the dash, strip
  // nickname parentheticals from the name, and DROP anything that can't yield a
  // full (≥2-token) name rather than surface a bare first-name fragment.
  state.assets.filter((a) => a.scope === 'executive' && !NOT_A_FOOTPRINT.has(a.assetType)).forEach((a) => {
    const parsed = parseExecName(a.title || '');
    if (!parsed) return; // unparseable fragment — omit, never show a first-name artifact
    ents.push({ key: 'exec-' + slugify(parsed.name), name: parsed.name, role: parsed.role, match: (x) => x === a });
  });
  return ents;
}

const _ROLE_WORDS = /\b(CEO|CFO|COO|CTO|chair|chairman|president|director|founder|officer|VP|head|owner|deputy|former)\b/i;

/** Parse an executive asset title into a full name + role, handling both title
 *  shapes agents emit: "Name (nickname) — Role, Company" (role after the dash)
 *  and "Name (Role) — Platform" (role in the parenthetical, platform suffix).
 *  Returns null when the name can't be resolved to at least two tokens — a
 *  first-name-only fragment is dropped rather than surfaced (C3). */
export function parseExecName(title: string): { name: string; role: string } | null {
  let t = (title || '').trim();
  if (!t) return null;
  // 1. Drop a trailing platform suffix ("— LinkedIn", "— Website", …) so it is
  //    never mistaken for the role.
  t = t.replace(/\s+[—–]\s+(Official\s+Site|Home\s*page|Website|LinkedIn|Facebook|X\/Twitter|Twitter\/X|Twitter|X|Instagram|YouTube|TikTok|Wikipedia|Wikidata|App)\s*$/i, '').trim();
  let name = t;
  let role = '';
  // 2. A parenthetical that NAMES a role is the role (and comes out of the name);
  //    otherwise it's a nickname and the role is whatever follows the dash.
  const paren = t.match(/\(([^)]*)\)/);
  if (paren && _ROLE_WORDS.test(paren[1] || '')) {
    role = (paren[1] || '').trim();
    name = t.replace(/\s*\([^)]*\)\s*/g, ' ').trim();
  } else {
    const dash = t.split(/\s+[—–]\s+/);
    if (dash.length > 1) { name = (dash[0] || '').trim(); role = dash.slice(1).join(' — ').trim(); }
    name = name.replace(/\([^)]*\)/g, ' ').replace(/\s+/g, ' ').trim(); // strip nickname parens
  }
  role = role.replace(/\([^)]*\)/g, ' ').replace(/\s+/g, ' ').trim() || 'Executive';
  if (name.split(/\s+/).filter(Boolean).length < 2) return null; // drop first-name fragments
  return { name, role };
}

/** Whether the run DECLARED its corporate entity map (payload.entities). When
 *  false, matrixEntities() derives sub-entities from asset scopes — a state the
 *  UI must label as such (C3). */
export function entityMapDeclared(state: AppPayloadState): boolean {
  return Array.isArray(state.entityMap) && state.entityMap.length > 0;
}

export const MATRIX_PLATFORMS: string[] = ['Website','LinkedIn','Facebook','X','Instagram','YouTube','TikTok','Wikipedia','Wikidata'];
export function platformOf(a: NormalizedAsset): string | null {
  if (a.platform && MATRIX_PLATFORMS.includes(a.platform)) return a.platform;
  if (a.assetType === 'website' || a.assetType === 'microsite') return 'Website';
  return null;
}
